import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeStudent, getMyStudents } from '@/services/parents';
import { parseAuthToken } from '@/utils/helpers/token';
import { getAccessTokenByApi } from '@/services/auth';
import { setAccessToken } from '@/services/axios-instance';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DropdownMenuSeparator } from '../ui/dropdown-menu';
import { useTranslation } from 'react-i18next';

const StudentSelectionForm: React.FC = () => {
  const queryClient = useQueryClient();
  const studentID = localStorage.getItem('studentID') || '';
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.userMenu',
  });

  const { data: studentsData, isLoading: isLoadingStudents } = useQuery({
    queryKey: ['my-students'],
    queryFn: () => getMyStudents(),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: changeStudent,
    onSuccess: async (data) => {
      const token = parseAuthToken(data.token);
      if (token.studentInfo?.studentID) {
        const resp = await getAccessTokenByApi();
        setAccessToken(resp.data.data.token);
        localStorage.setItem('studentID', token.studentInfo.studentID);
        queryClient.invalidateQueries();
      }
    },
  });

  const handleSelectChange = (value: string) => {
    localStorage.setItem('studentID', value);
    mutate({ studentID: value });
  };

  if (isLoadingStudents) return null;
  if (!isLoadingStudents && studentsData?.totalCount === 1) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground px-1">
          {t('select-student')}
        </p>
        <div className="flex-col gap-2 flex">
          <Select
            value={studentID}
            onValueChange={handleSelectChange}
            disabled={isPending}
          >
            <SelectTrigger className="w-full h-10 bg-background border-muted-foreground/20">
              <SelectValue placeholder="Öğrenci Seçiniz" />
            </SelectTrigger>
            <SelectContent>
              {studentsData?.items.map((student) => (
                <SelectItem key={student.id} value={student.id}>
                  {student.firstname} {student.lastname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};

export default StudentSelectionForm;
